import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppContext from '../../context/app/app.context';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, FreeMode, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';

import './Home.component.scss';
import slider1 from '../../assets/images/home-slider-1.png';
import slider2 from '../../assets/images/home-slider-2.png';
import slider3 from '../../assets/images/home-slider-3.png';
import slider4 from '../../assets/images/home-slider-4.png';
import homeVenueMap from '../../assets/images/home-venue-map.jpg';
import stormzyReadsHero from '../../assets/images/hero-image-1-stormzy-reads.jpg';
import pitchTeamHero from '../../assets/images/hero-image-2a.jpg';

import WatchVideoModal from '../../modal-pages/watch-video-modal/WatchVideoModal.component';
import { _bookingTypes } from '../../constants/booking-types';

const sliderImages = [slider1, slider2, slider3, slider4];


function Home() {
    const bookingTypes = useMemo(() => [..._bookingTypes], []);
    const { openModal } = useContext(AppContext);
    const [heroRealIndex, setHeroRealIndex] = useState(0);
    const [isWatchVideoModalOpen, setIsWatchVideoModalOpen] = useState(false);

    /** component will-mount */
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);


    const navigate = useNavigate();
    const onNavigateToBook = (category) => {
        navigate('booking', {
            state: category.type
        });
    };

    const onWatchCb = useCallback(() => {
        setIsWatchVideoModalOpen(true);
    }, []);
    const onBookingCb = useCallback(() => {
        navigate('booking', { state: 'pitch' })
    }, [navigate]);
    const onCupCb = useCallback(() => {
        navigate('/ecup');
    }, [navigate]);
    const onContactUsCb = useCallback(() => {
        openModal('contact');
    }, [openModal]);


    /** Optional per-slide CTAs: `booking` uses same navigation as booking tiles; `route` is absolute app path; `modal` opens app modals by name. */
    const _HERO_SLIDES = useMemo(() => ([
        {
            type: 'image',
            src: stormzyReadsHero,
            actions: [
                { label: 'WATCH', action: onWatchCb },
                { label: '#MERKY FC CUP', action: onCupCb },
            ]
        },
        {
            type: 'image',
            src: pitchTeamHero,
            actions: [
                { label: 'BOOK PITCH', action: onBookingCb },
                { label: 'CONTACT US', action: onContactUsCb }
            ]
        }
    ]), [onWatchCb, onBookingCb, onCupCb, onContactUsCb]);
    const heroSlideActions = useMemo(() => _HERO_SLIDES[heroRealIndex]?.actions ?? [], [_HERO_SLIDES, heroRealIndex]);


    const heroPagination = useMemo(() => ({
        clickable: true,
        renderBullet(_index, className) {
            return `<span class="${className}" role="presentation" aria-hidden="true"></span>`;
        }
    }), []);


    return (
        <div className="home-wrapper">
            <div className={`hero-section ${heroSlideActions.length ? 'hero-section--has-hero-actions' : 'hero-section--no-hero-actions'}`}>
                <Swiper className='hero-swiper'
                    modules={[Autoplay, EffectFade, Pagination]}
                    effect='fade'
                    fadeEffect={{ crossFade: true }}
                    loop={true}
                    speed={800}
                    autoplay={{
                        delay: 3500,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    }}
                    pagination={heroPagination}
                    onSlideChange={(swiper) => setHeroRealIndex(swiper.realIndex)}
                >
                    {_HERO_SLIDES.map((slide, i) => (
                        <SwiperSlide key={i}>
                            {slide.type === 'video' ? (
                                <video src={slide.src}
                                    className='hero-media'
                                    muted
                                    loop
                                    autoPlay
                                    playsInline
                                    preload='metadata'
                                />
                            ) : (
                                <img className='hero-media' src={slide.src} alt={`hero slide ${i + 1}`} draggable='false' />
                            )}
                        </SwiperSlide>
                    ))}
                </Swiper>

                {heroSlideActions.length > 0 ? (
                    <div className="hero-actions" role="group">
                        {heroSlideActions.map((item, idx) => (
                            <button
                                key={`${heroRealIndex}-${idx}`}
                                type="button"
                                className="hero-action-btn"
                                onClick={() => item?.action?.()}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>

            <WatchVideoModal
                isOpen={isWatchVideoModalOpen}
                onClose={() => setIsWatchVideoModalOpen(false)}
            />

            <div className='welcome-section'>
                <div className='welcome-section-img-wrapper'>
                    <img className='welcome-section-img' alt='' />
                </div>
            </div>

            <div className="image-slider-container">
                <Swiper className="image-slider-wrapper"
                    modules={[FreeMode]}
                    freeMode={true}
                    grabCursor={true}
                    slidesPerView="auto"
                    spaceBetween={15}
                >
                    {(sliderImages?.length > 0) && sliderImages.map((imgSrc, i) => (
                        <SwiperSlide key={i}>
                            <div className="image-item">
                                <img className='image-item-img' src={imgSrc} alt='' draggable='false' />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className='home-about-us-wrapper'>
                <h4 className='about-us-title'>about us</h4>
                <p className='about-us-description'>
                    #MERKY FC HQ IS A FOOTBALL AND COMMUNITY CENTRE IN SELHURST, DESIGNED TO NURTURE THE VOICES OF TOMORROW. PURPOSE-BUILT WITH A FULL SIZE 3G PITCH, RECORDING STUDIO AND GAMING ROOM, IT’S A SAFE SPACE FOR ALL TO EXPRESS THEMSELVES.
                    <br /><br />
                    RAN BY COMMUNITIES FIRST WHO ARE AN ORGANISATION THAT SUPPORT LOCAL COMMUNITIES AND YOUTH, ADIDAS AND #Merky FC WILL ACTIVATE IN THE SPACE THROUGHOUT THE YEAR, USING SPORT AND MUSIC AS THEIR MAIN FOCUS.
                </p>
            </div>


            <div className='home-booking-wrapper'>
                {
                    bookingTypes.map((bookItem, i) => {
                        return (
                            <div className='home-booking-item' key={i} onClick={() => onNavigateToBook(bookItem)}>
                                {/* <img className='home-booking-item-img' src={bookItem.img} alt='' /> */}
                                <picture>
                                    {bookItem.image?.list?.map((sourceItem, ii) => {
                                        return <source key={`${i}${ii}`} media={sourceItem?.media} srcSet={sourceItem?.srcset} />
                                    })}
                                    <img className='home-booking-item-img' srcSet={bookItem.image?.default} draggable="false" alt="Chris standing up holding his daughter Elva" />
                                </picture>
                                <div className='home-booking-item-details'>
                                    <h4 className='home-booking-item-title'>{bookItem.title}</h4>
                                    {/* <p className='home-booking-item-description'>{bookItem.description}</p> */}
                                </div>
                            </div>
                        );
                    })
                }
                {/* style={{backgroundImage: `url(${bookPitchM})`}} */}
                {/* <div className='home-booking-item'>
                    <img className='home-booking-item-img' src={bookPitchM} alt='' />
                    <div className='home-booking-item-details'>
                        <h4 className='home-booking-item-title'>book a pitch</h4>
                        <p className='home-booking-item-description'>Lorem ipsum dolor san e mono exrer ipsum dolor san e mono exrer ipsum dolor san e mono exrer ipsum dolor san e mono exrer </p>
                    </div>
                </div> */}
                {/* <div className='home-booking-item'>
                    <h4 className='home-venue-hire-link'>
                        venue hire coming soon
                    </h4>
                </div> */}
            </div>


            <div className='home-venue-map-wrapper'>
                <div className='home-venue-map'>
                    <img className='home-venue-map-img' src={homeVenueMap} draggable='false' alt='mfchub venue' />
                </div>
            </div>


            <div className='home-address-container'>
                <div className='footer-logo'></div>

                <div className='home-address-wrapper'>
                    <div className='home-address-item'>
                        <h4 className='address-label'>opening times</h4>
                        <h4 className='address-details'>Mon - Sun<br />8am - 10pm</h4>
                    </div>
                    <div className='home-address-divider'></div>
                    <div className='home-address-item'>
                        <h4 className='address-label'>location</h4>
                        <h4 className='address-details'>Dagnall Park,<br />London,<br />SE25 5PH</h4>
                    </div>
                    <div className='home-address-divider'></div>
                    <div className='home-address-item'>
                        <h4 className='address-label'>#merky fc hq</h4>
                        <h4 className='address-details'>CHANGING <br />THE GAME</h4>
                    </div>
                </div>
            </div>
        </div>
    )
}


export default Home;